import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type RefinementListProps, useRefinementList } from 'react-instantsearch';
import c from 'classnames';

import { CONTENT_TYPES } from '../../../config/constants';

export interface SearchTypeFacetProps extends RefinementListProps {
  className?: string;
}

/**
 * SearchTypeFacet Component
 *
 * A specialized facet component for filtering search results by page types.
 * Provides a checkbox to filter for general pages (basic_page, landing_page, overview_page).
 *
 * @param props - The component props extending RefinementListProps
 * @param props.className - Optional CSS class name for styling customization
 * @param props.attribute - The search attribute to refine on (defaults to 'combined_type_filter')
 *
 * @returns The rendered search type facet component or null if refinement is not available
 */
const SearchTypeFacet: React.FC<SearchTypeFacetProps> = (props) => {
  const { className } = props;
  const { t } = useTranslation();

  const {
    items,
    canRefine,
    refine,
  } = useRefinementList({
    attribute: 'combined_type_filter',
  });

  /**
   * Page types that should be considered as "general pages"
   */
  const GENERAL_PAGE_TYPES = [
    CONTENT_TYPES.BASIC_PAGE,
    CONTENT_TYPES.LANDING_PAGE,
    CONTENT_TYPES.OVERVIEW_PAGE,
  ] as const;

  /**
   * Toggles refinement for all general page types
   */
  const handleTypeRefinementToggle = (): void => {
    GENERAL_PAGE_TYPES.forEach((pageType) => {
      refine(pageType);
    });
  };

  /**
   * Determines if any of the general page types are currently refined.
   */
  const isRefined = useMemo((): boolean => items.some(
    (item) => item.label === CONTENT_TYPES.BASIC_PAGE && item.isRefined,
  ), [items]);

  /**
   * Don't render the component if refinement is not available, or only document type activated.
   */
  if (!canRefine || (items.length === 1 && items[0]?.label === CONTENT_TYPES.DOCUMENT)) {
    return null;
  }

  const wrapperClasses = c(
    'Searchkit__Facet__RefinementCheckboxes__wrapper',
    className,
  );

  const checkboxClasses = c(
    'WebformCheckboxes__checkbox__input',
    'icon-before',
    'icon-checkmark',
    `icon-variant-${isRefined ? '1' : '0'}`,
  );

  const generalPagesLabel = t('searchkit.general_pages');

  return (
    <div className={wrapperClasses}>
      <div className="FormElement Checkboxes Searchkit__Facet__RefinementCheckboxes Checkboxes">
        <div className="FormElement__inner Checkboxes Searchkit__Facet__RefinementCheckboxes Checkboxes__inner">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
           jsx-a11y/no-noninteractive-element-interactions */}
          <label
            className={checkboxClasses}
            id="searchTypeFacet"
            htmlFor="refinementList_type_filter"
            onClick={handleTypeRefinementToggle}
          >
            <input
              name="refinementList_type_filter"
              type="checkbox"
              id="refinementList_combined_type_name--All"
              className="Searchkit__Facet__RefinementCheckboxes__item Checkboxes__item"
              value={generalPagesLabel}
              checked={isRefined}
            />
            <span className="Searchkit__Facet__label__label">
              {generalPagesLabel}
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default SearchTypeFacet;
